import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import Consulting from './pages/Consulting';
import Quotes from './pages/Quotes';
import Policies from './pages/Policies';
import Claims from './pages/Claims';
import Renewals from './pages/Renewals';
import Payments from './pages/Payments';
import TelecomContracts from './pages/TelecomContracts';
import TelecomSIMs from './pages/TelecomSIMs';
import TelecomBilling from './pages/TelecomBilling';
import UserManagement from './pages/UserManagement';
import SearchResults from './pages/SearchResults';
import AIAssistant from './pages/AIAssistant';
import CanoneRai from './pages/CanoneRai';
import CompanyProfile from './pages/CompanyProfile';
import Mutui from './pages/Mutui';
import Delega from './pages/Delega';

const ProtectedRoute = ({ children, requiredModule, adminOnly }) => {
  const { user, loading, hasModuleAccess, isAdmin } = useAuth();

  // Show loading spinner while authentication state is being determined
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  // Only redirect to login if we're certain user is not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Admin-only route check
  if (adminOnly && !isAdmin()) {
    // Find the first accessible route for this user
    const defaultRoute = user.modules_access?.includes('general') ? '/clients' : '/login';
    return <Navigate to={defaultRoute} replace />;
  }

  // Module access check
  if (requiredModule && !hasModuleAccess(requiredModule)) {
    // Redirect to a route the user has access to
    if (isAdmin()) {
      return <Navigate to="/dashboard" replace />;
    }
    const defaultRoute = user.modules_access?.includes('general') ? '/clients' : '/login';
    return <Navigate to={defaultRoute} replace />;
  }

  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to={user.role === 'admin' ? '/dashboard' : '/clients'} replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      
      {/* Admin Dashboard */}
      <Route path="/dashboard" element={<ProtectedRoute adminOnly><Dashboard /></ProtectedRoute>} />
      
      {/* General CRM */}
      <Route path="/clients" element={<ProtectedRoute requiredModule="general"><Clients /></ProtectedRoute>} />
      <Route path="/delega" element={<ProtectedRoute requiredModule="general"><Delega /></ProtectedRoute>} />
      <Route path="/company-profile" element={<ProtectedRoute adminOnly><CompanyProfile /></ProtectedRoute>} />
      
      {/* Consulting */}
      <Route path="/consulting" element={<ProtectedRoute requiredModule="consulting"><Consulting /></ProtectedRoute>} />
      
      {/* Services */}
      <Route path="/canone-rai" element={<ProtectedRoute requiredModule="insurance"><CanoneRai /></ProtectedRoute>} />
      <Route path="/mutui" element={<ProtectedRoute requiredModule="insurance"><Mutui /></ProtectedRoute>} />
      
      {/* Insurance CRM */}
      <Route path="/quotes" element={<ProtectedRoute requiredModule="insurance"><Quotes /></ProtectedRoute>} />
      <Route path="/policies" element={<ProtectedRoute requiredModule="insurance"><Policies /></ProtectedRoute>} />
      <Route path="/claims" element={<ProtectedRoute requiredModule="insurance"><Claims /></ProtectedRoute>} />
      <Route path="/renewals" element={<ProtectedRoute requiredModule="insurance"><Renewals /></ProtectedRoute>} />
      <Route path="/payments" element={<ProtectedRoute requiredModule="insurance"><Payments /></ProtectedRoute>} />
      
      {/* Telecom CRM */}
      <Route path="/telecom/contracts" element={<ProtectedRoute requiredModule="telecom"><TelecomContracts /></ProtectedRoute>} />
      <Route path="/telecom/sims" element={<ProtectedRoute requiredModule="telecom"><TelecomSIMs /></ProtectedRoute>} />
      <Route path="/telecom/billing" element={<ProtectedRoute requiredModule="telecom"><TelecomBilling /></ProtectedRoute>} />
      
      {/* Admin Only */}
      <Route path="/users" element={<ProtectedRoute adminOnly><UserManagement /></ProtectedRoute>} />
      
      {/* Common */}
      <Route path="/search" element={<ProtectedRoute><SearchResults /></ProtectedRoute>} />
      <Route path="/ai-assistant" element={<ProtectedRoute><AIAssistant /></ProtectedRoute>} />
      
      {/* Redirects */}
      <Route path="/" element={<Navigate to="/clients" replace />} />
      <Route path="*" element={<Navigate to="/clients" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
